import { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Folder as FolderIcon, ChevronsUpDown, Check } from 'lucide-react';

/**
 * Searchable Documents-Hub folder picker. Shows the full breadcrumb path
 * for each folder (e.g. "Smith Family / Signed Documents") so the user can
 * search anywhere in the hierarchy and immediately know which subfolder
 * they're targeting. Special value `__root` represents the Documents Hub
 * root (no specific folder).
 *
 * Extracted from pages/SignaturesHub.jsx.
 */
export function FolderBrowserPicker({ folders, value, onChange }) {
  const [open, setOpen] = useState(false);

  // Build a path string ("Smith / Signed Documents") for each folder so the
  // search input and the readable display both show the full hierarchy.
  const folderById = useMemo(() => {
    const m = new Map();
    folders.forEach((f) => m.set(f.folder_id, f));
    return m;
  }, [folders]);

  const buildPath = (fid) => {
    const parts = [];
    let cur = folderById.get(fid);
    let safety = 0;
    while (cur && safety < 12) {
      parts.unshift(cur.name);
      cur = cur.parent_folder_id ? folderById.get(cur.parent_folder_id) : null;
      safety++;
    }
    return parts.join(' / ');
  };

  const selectedLabel = value === '__root'
    ? 'Documents Hub (root)'
    : (buildPath(value) || 'Documents Hub (root)');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid="folder-picker-trigger"
        >
          <span className="flex items-center gap-2 min-w-0 truncate">
            <FolderIcon className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <span className="truncate">{selectedLabel}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search folders…" className="h-9" />
          <CommandList className="max-h-80">
            <CommandEmpty>No folders found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="documents hub root"
                onSelect={() => { onChange('__root'); setOpen(false); }}
                data-testid="folder-option-root"
              >
                <Check className={`mr-2 h-3.5 w-3.5 ${value === '__root' ? 'opacity-100' : 'opacity-0'}`} />
                <FolderIcon className="w-4 h-4 text-emerald-600 mr-2" />
                Documents Hub (root)
              </CommandItem>
              {folders.map((f) => (
                <CommandItem
                  key={f.folder_id}
                  value={buildPath(f.folder_id)}
                  onSelect={() => { onChange(f.folder_id); setOpen(false); }}
                  data-testid={`folder-option-${f.folder_id}`}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${value === f.folder_id ? 'opacity-100' : 'opacity-0'}`} />
                  <FolderIcon className="w-4 h-4 text-emerald-600 mr-2 flex-shrink-0" />
                  <span className="truncate">{buildPath(f.folder_id)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default FolderBrowserPicker;
